import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { ArrowLeft, Mail, Lock, User, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { IconFormField } from "@/components/auth/IconFormField";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import logo from "@/assets/logo-bebloo.png";

const loginSchema = z.object({
  email: z.string().trim().email({ message: "Email inválido" }),
  password: z.string().min(6, { message: "Mínimo 6 caracteres" }),
});

const signupSchema = z.object({
  fullName: z.string().trim().min(2, { message: "Nombre muy corto" }).max(100, { message: "Nombre muy largo" }),
  email: z.string().trim().email({ message: "Email inválido" }),
  password: z.string().min(6, { message: "Mínimo 6 caracteres" }),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Las contraseñas no coinciden",
  path: ["confirmPassword"],
});

type LoginFormValues = z.infer<typeof loginSchema>;
type SignupFormValues = z.infer<typeof signupSchema>;

export default function Auth() {
  const [activeTab, setActiveTab] = useState<"login" | "signup">("login");
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signIn, signUp, signInWithGoogle } = useAuth();
  const { toast } = useToast();

  const locationState = location.state as {
    from?: string | { pathname: string };
    selections?: Record<string, boolean>;
  } | null;

  // Determine redirect target
  const fromPath = typeof locationState?.from === "string"
    ? locationState.from
    : locationState?.from?.pathname || "/app";

  const isPlanFlow = fromPath.startsWith("/packs/") && fromPath.includes("/checkout");

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      if (isPlanFlow) {
        navigate(fromPath, {
          replace: true,
          state: { selections: locationState?.selections, fromAuth: true },
        });
      } else {
        navigate(fromPath, { replace: true });
      }
    }
  }, [user, navigate, fromPath, isPlanFlow, locationState?.selections]);

  const loginForm = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const signupForm = useForm<SignupFormValues>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      fullName: "",
      email: "",
      password: "",
      confirmPassword: "",
    },
  });

  const onLogin = async (values: LoginFormValues) => {
    setIsLoading(true);
    const { error } = await signIn(values.email, values.password);
    setIsLoading(false);

    if (error) {
      toast({
        variant: "destructive",
        title: "Error al iniciar sesión",
        description: error.message === "Invalid login credentials"
          ? "Email o contraseña incorrectos"
          : error.message,
      });
    }
  };

  const onSignup = async (values: SignupFormValues) => {
    setIsLoading(true);
    const { error } = await signUp(values.email, values.password, values.fullName);
    setIsLoading(false);

    if (error) {
      const errorMessages: Record<string, string> = {
        "User already registered": "Este email ya está registrado",
      };
      toast({
        variant: "destructive",
        title: "Error al crear cuenta",
        description: errorMessages[error.message] || error.message,
      });
    } else {
      toast({
        title: "¡Cuenta creada!",
        description: "Revisa tu email para confirmar tu cuenta.",
      });
      setActiveTab("login");
    }
  };

  const handleGoogleSignIn = async () => {
    setIsGoogleLoading(true);
    const { error } = await signInWithGoogle();
    setIsGoogleLoading(false);

    if (error) {
      toast({
        variant: "destructive",
        title: "Error con Google",
        description: error.message,
      });
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="p-4 md:p-6">
        <div className="container max-w-6xl">
          <Button
            variant="ghost"
            onClick={() => navigate("/")}
            className="gap-2 text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            Volver
          </Button>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md space-y-8">
          {/* Logo and welcome message */}
          <div className="text-center space-y-4">
            <img src={logo} alt="bebloo" className="h-12 mx-auto" />
            <div>
              <h1 className="text-2xl font-display font-medium text-foreground">
                Bienvenido/a a bebloo
              </h1>
              <p className="text-muted-foreground mt-2">
                Tu compañero en cada etapa del camino
              </p>
            </div>
          </div>

          {/* Google Sign In */}
          <Button
            variant="outline"
            className="w-full gap-3 h-12"
            onClick={handleGoogleSignIn}
            disabled={isGoogleLoading || isLoading}
          >
            {isGoogleLoading ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <svg className="h-5 w-5" viewBox="0 0 24 24">
                <path
                  fill="currentColor"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="currentColor"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="currentColor"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="currentColor"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
            )}
            Continuar con Google
          </Button>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">
                o con email
              </span>
            </div>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as "login" | "signup")}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="login">Iniciar sesión</TabsTrigger>
              <TabsTrigger value="signup">Crear cuenta</TabsTrigger>
            </TabsList>

            {/* Login Form */}
            <TabsContent value="login" className="mt-6">
              <Form {...loginForm}>
                <form onSubmit={loginForm.handleSubmit(onLogin)} className="space-y-4">
                  <IconFormField control={loginForm.control} name="email" label="Email" icon={Mail} placeholder="tu@email.com" />
                  <IconFormField control={loginForm.control} name="password" label="Contraseña" icon={Lock} type="password" placeholder="••••••••" />
                  <Button type="submit" className="w-full h-11" disabled={isLoading}>
                    {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Iniciar sesión
                  </Button>
                </form>
              </Form>
            </TabsContent>

            {/* Signup Form */}
            <TabsContent value="signup" className="mt-6">
              <Form {...signupForm}>
                <form onSubmit={signupForm.handleSubmit(onSignup)} className="space-y-4">
                  <IconFormField control={signupForm.control} name="fullName" label="Nombre completo" icon={User} placeholder="Tu nombre" />
                  <IconFormField control={signupForm.control} name="email" label="Email" icon={Mail} placeholder="tu@email.com" />
                  <IconFormField control={signupForm.control} name="password" label="Contraseña" icon={Lock} type="password" placeholder="••••••••" />
                  <IconFormField control={signupForm.control} name="confirmPassword" label="Confirmar contraseña" icon={Lock} type="password" placeholder="••••••••" />
                  <Button type="submit" className="w-full h-11" disabled={isLoading}>
                    {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Crear cuenta
                  </Button>
                </form>
              </Form>
            </TabsContent>
          </Tabs>

          {/* Footer note */}
          <p className="text-center text-xs text-muted-foreground">
            Al continuar, aceptas nuestros{" "}
            <Link to="/condiciones" className="underline hover:text-foreground transition-colors">términos de servicio</Link>
            {" "}y{" "}
            <Link to="/privacidad" className="underline hover:text-foreground transition-colors">política de privacidad</Link>.
          </p>
        </div>
      </main>
    </div>
  );
}
